// Batch upload: sign URLs, PUT to GCS 5 at a time, finalize in chunks.
// A failed finalize records nothing server-side, so all rows read failed.
import { useCallback } from 'react';
import axios from 'axios';
import clientService from '../clientService';
import {
  FileUploadRequest,
  FileFinalizeRequest,
  FileAdminDetailWithUser,
} from '../api/Api';

const MAX_CONCURRENT_UPLOADS = 5;

const FINALIZE_CHUNK_SIZE = 100;

const EXTENSION_TO_MIME: Record<string, string> = {
  wav: 'audio/wav',
  mp3: 'audio/mpeg',
  flac: 'audio/flac',
  ogg: 'audio/ogg',
  m4a: 'audio/mp4',
};

function resolveContentType(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_TO_MIME[ext] || 'application/octet-stream';
}

export interface UploadFileItem {
  file: File;
  id: string;
  name?: string;
}

export interface UploadResult {
  id: string;
  file: File;
  fileRecord: FileAdminDetailWithUser | null;
  error?: string;
}

export type UploadPhase = 'idle' | 'uploading' | 'done';

export type FileUploadStatus = 'queued' | 'uploading' | 'done' | 'error';

export interface BatchFileProgress {
  id: string;
  name: string;
  pct: number;
  status: FileUploadStatus;
}

export interface BatchProgress {
  phase: UploadPhase;
  uploadedCount: number;
  totalCount: number;
  failedCount: number;
  overallPct: number;
  files: BatchFileProgress[];
}

interface UseFileUploadResult {
  uploadFile: (
    file: File,
    deploymentId: string,
    onProgress: (progress: number) => void,
  ) => Promise<FileAdminDetailWithUser | null>;
  uploadFiles: (
    items: UploadFileItem[],
    deploymentId: string,
    onBatchProgress: (progress: BatchProgress) => void,
  ) => Promise<UploadResult[]>;
}

interface GcsUploadError {
  response?: { status?: number };
  message?: string;
}

function createSemaphore(limit: number) {
  let active = 0;
  const queue: (() => void)[] = [];

  const acquire = (): Promise<void> =>
    new Promise((resolve) => {
      if (active < limit) {
        active++;
        resolve();
      } else {
        queue.push(() => {
          active++;
          resolve();
        });
      }
    });

  const release = () => {
    active--;
    const next = queue.shift();
    if (next) next();
  };

  return { acquire, release };
}

const useFileUpload = (): UseFileUploadResult => {
  const uploadFile = useCallback(
    async (
      file: File,
      deploymentId: string,
      onProgress: (progress: number) => void,
    ): Promise<FileAdminDetailWithUser | null> => {
      try {
        const uploadRequest: FileUploadRequest = {
          deployment_id: deploymentId,
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
        };
        const generateUrlResponse =
          await clientService.api.generateUploadUrlApiV1AdminFilesGenerateUploadUrlPost(
            uploadRequest,
          );
        const { upload_url, gcs_path } = generateUrlResponse.data;

        await axios.put(upload_url, file, {
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              onProgress(percentCompleted);
            }
          },
        });

        const finalizeRequest: FileFinalizeRequest = {
          deployment_id: deploymentId,
          gcs_path: gcs_path,
          file_name: file.name,
        };
        const finalizeResponse =
          await clientService.api.finalizeUploadApiV1AdminFilesFinalizeUploadPost(
            finalizeRequest,
          );

        return finalizeResponse.data;
      } catch (err) {
        console.error('File upload failed:', err);
        return null;
      }
    },
    [],
  );

  const uploadFiles = useCallback(
    async (
      items: UploadFileItem[],
      deploymentId: string,
      onBatchProgress: (progress: BatchProgress) => void,
    ): Promise<UploadResult[]> => {
      if (items.length === 0) return [];

      const totalCount = items.length;
      const totalBytes = items.reduce((sum, i) => sum + i.file.size, 0);

      const bytesUploadedPerFile = new Map<string, number>(
        items.map((i) => [i.id, 0]),
      );
      const statusPerFile = new Map<string, FileUploadStatus>(
        items.map((i) => [i.id, 'queued']),
      );
      let uploadedCount = 0;
      let failedCount = 0;

      const emitProgress = (phase: UploadPhase) => {
        const totalUploaded = [...bytesUploadedPerFile.values()].reduce(
          (a, b) => a + b,
          0,
        );
        const overallPct =
          totalBytes > 0 ? Math.round((totalUploaded / totalBytes) * 100) : 0;
        onBatchProgress({
          phase,
          uploadedCount,
          totalCount,
          failedCount,
          overallPct,
          files: items.map((item) => ({
            id: item.id,
            name: nameById.get(item.id) as string,
            pct:
              item.file.size > 0
                ? Math.min(
                    100,
                    Math.round(
                      ((bytesUploadedPerFile.get(item.id) ?? 0) / item.file.size) *
                        100,
                    ),
                  )
                : 100,
            status: statusPerFile.get(item.id) ?? 'queued',
          })),
        });
      };

      const nameById = new Map<string, string>(
        items.map((i) => [i.id, i.name ?? i.file.name]),
      );

      emitProgress('uploading');

      const urlMap: Record<string, { upload_url: string; gcs_path: string }> = {};

      try {
        const batchUrlResponse =
          await clientService.api.batchGenerateUploadUrlsApiV1AdminFilesGenerateUploadUrlsPost(
            {
              deployment_id: deploymentId,
              files: items.map((item) => ({
                file_name: nameById.get(item.id) as string,
                content_type: resolveContentType(item.file),
              })),
            },
          );

        for (const entry of batchUrlResponse.data) {
          urlMap[entry.file_name] = {
            upload_url: entry.upload_url,
            gcs_path: entry.gcs_path,
          };
        }
      } catch (err) {
        console.error('Batch generate upload URLs failed:', err);
        failedCount = totalCount;
        items.forEach((item) => statusPerFile.set(item.id, 'error'));
        emitProgress('done');
        return items.map((item) => ({
          id: item.id,
          file: item.file,
          fileRecord: null,
          error: 'Failed to get upload URLs from server',
        }));
      }

      emitProgress('uploading');

      const semaphore = createSemaphore(MAX_CONCURRENT_UPLOADS);

      const gcsResults: Array<{
        id: string;
        file: File;
        name: string;
        gcs_path: string | null;
        error?: string;
      }> = await Promise.all(
        items.map(async (item) => {
          const name = nameById.get(item.id) as string;
          const urlEntry = urlMap[name];
          if (!urlEntry) {
            failedCount++;
            statusPerFile.set(item.id, 'error');
            bytesUploadedPerFile.set(item.id, item.file.size);
            emitProgress('uploading');
            return {
              id: item.id,
              file: item.file,
              name,
              gcs_path: null,
              error: 'No signed URL received for this file',
            };
          }

          const contentType = resolveContentType(item.file);

          await semaphore.acquire();
          try {
            let attempt = 0;
            let success = false;
            while (attempt < 3 && !success) {
              try {
                await axios.put(urlEntry.upload_url, item.file, {
                  headers: { 'Content-Type': contentType },
                  onUploadProgress: (progressEvent) => {
                    statusPerFile.set(item.id, 'uploading');
                    bytesUploadedPerFile.set(item.id, progressEvent.loaded);
                    emitProgress('uploading');
                  },
                });
                success = true;
              } catch (rawErr) {
                const err = rawErr as GcsUploadError;
                attempt++;
                const status = err.response?.status;
                if (attempt < 3 && (!status || status >= 500)) {
                  const delay = Math.min(
                    5000,
                    1000 * Math.pow(2, attempt) + Math.random() * 1000,
                  );
                  await new Promise((res) => setTimeout(res, delay));
                } else {
                  throw rawErr;
                }
              }
            }

            bytesUploadedPerFile.set(item.id, item.file.size);
            statusPerFile.set(item.id, 'done');
            uploadedCount++;
            emitProgress('uploading');
            return { id: item.id, file: item.file, name, gcs_path: urlEntry.gcs_path };
          } catch (rawErr) {
            const err = rawErr as GcsUploadError;
            console.error(`GCS upload failed for ${name}:`, err);
            bytesUploadedPerFile.set(item.id, item.file.size);
            statusPerFile.set(item.id, 'error');
            failedCount++;
            emitProgress('uploading');

            const status = err?.response?.status;
            const errorMessage =
              status === 400
                ? `Upload rejected (400) for "${name}" — Content-Type mismatch. Resolved type: "${contentType}".`
                : err?.message || 'GCS upload failed';

            return {
              id: item.id,
              file: item.file,
              name,
              gcs_path: null,
              error: errorMessage,
            };
          } finally {
            semaphore.release();
          }
        }),
      );

      const succeeded = gcsResults.filter((r) => r.gcs_path !== null);
      const failed = gcsResults.filter((r) => r.gcs_path === null);

      const finalizedFiles: FileAdminDetailWithUser[] = [];

      if (succeeded.length > 0) {
        try {
          for (let i = 0; i < succeeded.length; i += FINALIZE_CHUNK_SIZE) {
            const chunk = succeeded.slice(i, i + FINALIZE_CHUNK_SIZE);
            const finalizeResponse =
              await clientService.api.batchFinalizeUploadsApiV1AdminFilesFinalizeUploadsPost(
                {
                  deployment_id: deploymentId,
                  files: chunk.map((r) => ({
                    gcs_path: r.gcs_path as string,
                    file_name: r.name,
                  })),
                },
              );
            finalizedFiles.push(...finalizeResponse.data.files);
          }
        } catch (err) {
          console.error('Batch finalize failed:', err);
          failedCount = totalCount;
          items.forEach((item) => statusPerFile.set(item.id, 'error'));
          emitProgress('done');
          return [
            ...succeeded.map((r) => ({
              id: r.id,
              file: r.file,
              fileRecord: null,
              error:
                'Server finalize failed after GCS upload — files are safely uploaded, please retry or contact support.',
            })),
            ...failed.map((r) => ({
              id: r.id,
              file: r.file,
              fileRecord: null,
              error: r.error,
            })),
          ];
        }
      }

      emitProgress('done');

      const fileRecordByName = new Map<string, FileAdminDetailWithUser>(
        finalizedFiles.map((f) => [f.name, f]),
      );

      return [
        ...succeeded.map((r) => ({
          id: r.id,
          file: r.file,
          fileRecord: fileRecordByName.get(r.name) ?? null,
        })),
        ...failed.map((r) => ({
          id: r.id,
          file: r.file,
          fileRecord: null,
          error: r.error,
        })),
      ];
    },
    [],
  );

  return { uploadFile, uploadFiles };
};

export default useFileUpload;
